export type UrlValue = string | number | boolean;
export type UrlRule = { values?: readonly string[]; max?: number; uuid?: boolean };
export type UrlRules<T> = Partial<Record<keyof T, UrlRule>>;

/** Pages are zero-based internally and one-based in shareable URLs. */
export function readUrlState<T extends Record<string, UrlValue>>(search: string, defaults: T, rules: UrlRules<T> = {}): T {
  const params = new URLSearchParams(search);
  const result = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof T & string)[]) {
    const raw = params.get(key), fallback = defaults[key], rule = rules[key];
    if (raw === null) continue;
    let value: UrlValue = fallback;
    if (typeof fallback === 'number') {
      const n = Number(raw);
      if (/^[1-9]\d*$/.test(raw) && Number.isSafeInteger(n) && n <= (rule?.max ?? 101)) value = n - 1;
    } else if (typeof fallback === 'boolean') {
      if (raw === 'true' || raw === 'false') value = raw === 'true';
    } else if ((!rule?.values || rule.values.includes(raw)) && (!rule?.uuid || /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(raw))) {
      value = raw.slice(0, rule?.max ?? 120);
    }
    result[key] = value as T[keyof T & string];
  }
  return result;
}

export function writeUrlState<T extends Record<string, UrlValue>>(url: URL, defaults: T, state: T): URL {
  const next = new URL(url);
  for (const key of Object.keys(defaults)) {
    const value = state[key];
    if (value === defaults[key]) next.searchParams.delete(key);
    else next.searchParams.set(key, String(typeof value === 'number' ? value + 1 : value));
  }
  return next;
}
